import { Button } from './Button'

// ---------------------------------------------------------------------------
// Pagination — presentational keyset "load more" control (props in → UI out).
//
// The public register API paginates by an opaque keyset cursor
// (`meta.next_cursor`), not page numbers — there is no total page count, only a
// "there is a next page" signal. This control therefore renders a result-count
// line plus a single "load more" affordance, shown only while more rows remain.
//
// Purely presentational: the caller owns the cursor and the fetch. No data
// fetching, no auth logic, no privileged strings — the count line and button
// label are caller-supplied (already i18n'd). Safe for the public, sessionless
// register bundle.
//
// CSS: leans on the .filter-bar flex strip and the design-system .btn (via the
// Button wrapper); the count line is class-free muted text supplied by caller.
// ---------------------------------------------------------------------------

export type PaginationProps = {
  /** Human-readable count summary, already i18n'd (e.g. "Showing 20 of ~1234"). */
  countLabel: string
  /** True when a next-page cursor exists — controls whether "load more" shows. */
  hasMore: boolean
  /** "Load more" button label, already i18n'd by the caller. */
  loadMoreLabel: string
  /** Invoked when the user activates "load more". */
  onLoadMore: () => void
  /** Disables the button while a fetch is in flight. */
  loading?: boolean
}

/**
 * Keyset "load more" pagination control with a result-count line.
 *
 * Usage:
 *   <Pagination
 *     countLabel={t('register.lookup.result_count', { shown, total })}
 *     hasMore={nextCursor != null}
 *     loadMoreLabel={t('register.lookup.load_more')}
 *     onLoadMore={loadMore}
 *     loading={loading}
 *   />
 */
export function Pagination({
  countLabel,
  hasMore,
  loadMoreLabel,
  onLoadMore,
  loading = false,
}: PaginationProps) {
  return (
    <nav className="filter-bar" aria-label={countLabel}>
      <span className="muted">{countLabel}</span>
      {hasMore && (
        <>
          <span className="filter-bar__sp" aria-hidden="true" />
          <Button variant="ghost" disabled={loading} onClick={onLoadMore}>
            {loadMoreLabel}
          </Button>
        </>
      )}
    </nav>
  )
}
